import React, { useEffect, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useStore from "../../lib/store";
import { fuzzyMatch } from "../../lib/fuzzy";
import { getAllProjects } from "../../lib/loadContent";

/* ---- Build the searchable index (once at module load) ---- */
function buildIndex() {
  const items = [];
  [
    ["Dashboard", "Home / landing", "/"],
    ["About", "About me", "/about"],
    ["Projects", "Project listing", "/projects"],
    ["Resume", "My resume", "/resume"],
    ["Contact", "Get in touch", "/contact"],
  ].forEach(([label, desc, route]) =>
    items.push({ type: "Pages", label, description: desc, route }),
  );
  getAllProjects().forEach((p) =>
    items.push({
      type: "Projects",
      label: p.title,
      description: p.tagline,
      route: `/projects/${p.slug}`,
    }),
  );
  items.push({
    type: "Commands",
    label: ":help",
    description: "Show keyboard shortcuts",
    command: "help",
  });
  items.push({
    type: "Commands",
    label: ":theme dark",
    description: "Switch to dark theme",
    theme: "dark",
  });
  items.push({
    type: "Commands",
    label: ":theme light",
    description: "Switch to light theme",
    theme: "light",
  });
  return items;
}

const SEARCH_INDEX = buildIndex();

/* ---- Component ---- */
const Telescope = () => {
  const navigate = useNavigate();
  const {
    searchOpen,
    searchQuery,
    searchResults,
    searchIndex,
    setSearchQuery,
    setSearchResults,
    setSearchIndex,
    closeSearch,
    openWhichKey,
    setTheme,
  } = useStore();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Filter results on query change
  useEffect(() => {
    if (!searchOpen) return;
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setSearchIndex(0);
      return;
    }
    const results = SEARCH_INDEX.filter(
      (item) => fuzzyMatch(q, item.label) || fuzzyMatch(q, item.description),
    );
    setSearchResults(results);
    setSearchIndex(0);
  }, [searchQuery, searchOpen, setSearchResults, setSearchIndex]);

  // Auto-focus
  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  // Scroll active result into view
  useEffect(() => {
    if (!listRef.current || !searchResults.length) return;
    const active = listRef.current.querySelector(".telescope-item.active");
    if (active) active.scrollIntoView({ block: "nearest" });
  }, [searchIndex, searchResults.length]);

  const handleSelect = (idx) => {
    const item = searchResults[idx];
    if (!item) return;
    if (item.command === "help") {
      openWhichKey();
      closeSearch();
      return;
    }
    if (item.theme) {
      setTheme(item.theme);
      closeSearch();
      return;
    }
    navigate(item.route);
    closeSearch();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeSearch();
    } else if (e.key === "ArrowDown" || (e.key === "j" && e.ctrlKey)) {
      e.preventDefault();
      const len = searchResults.length;
      if (len > 0) setSearchIndex((searchIndex + 1) % len);
    } else if (e.key === "ArrowUp" || (e.key === "k" && e.ctrlKey)) {
      e.preventDefault();
      const len = searchResults.length;
      if (len > 0) setSearchIndex((searchIndex - 1 + len) % len);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(searchIndex);
    }
  };

  // Group results for display
  let lastType = null;

  return (
    <AnimatePresence>
      {searchOpen && (
        <motion.div
          className="telescope-backdrop"
          onClick={closeSearch}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <motion.div
            className="telescope"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -20 }}
            transition={{ duration: 0.15 }}
          >
            <input
              ref={inputRef}
              className="telescope-input"
              type="text"
              placeholder="Search pages, projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              autoComplete="off"
            />
            <div className="telescope-results" ref={listRef}>
              {searchResults.length === 0 && searchQuery.trim() !== "" && (
                <div className="telescope-empty">No results</div>
              )}
              {searchResults.map((item, i) => {
                const showHeader = item.type !== lastType;
                lastType = item.type;
                return (
                  <Fragment key={i}>
                    {showHeader && (
                      <div className="telescope-group">{item.type}</div>
                    )}
                    <button
                      type="button"
                      className={
                        "telescope-item" + (i === searchIndex ? " active" : "")
                      }
                      onClick={() => handleSelect(i)}
                      onMouseEnter={() => setSearchIndex(i)}
                    >
                      <span className="telescope-label">{item.label}</span>
                      <span className="telescope-desc">{item.description}</span>
                    </button>
                  </Fragment>
                );
              })}
            </div>
            <div className="telescope-footer">
              <span>↑↓ navigate</span>
              <span>↵ select</span>
              <span>esc close</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Telescope;
